import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, TextField, Divider, FormControl, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { RiDeleteBinLine } from 'react-icons/ri';
import {
  IRecruiterDTO,
  IWorkForceUser,
  postAdvancedQuery,
  removeWorkForceUser,
  sharePhoneNumber,
} from 'wisestep-api';
import {
  SearchBarNew,
  WsAddUserCard,
  WsAutoComplete,
  WsButton,
  WsDataGrid,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { array, object, string } from 'yup';

import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import {
  mapSharedAccount,
  recruiterAdvancedQueryPayload,
  sharePhoneNumberPayload,
} from '../helper/payload';

interface IAccountSetting {
  userId: string;
  phoneNumber: string;
  accountId: string;
  initialAssignedRecruiters: string[];
  refreshLocalStorage: () => Promise<void>;
  serviceId: string;
}

export interface IAssignedUsers {
  id?: string;
}

const schema = object().shape({
  assignedRecruiters: array()
    .of(string().email('Please enter a valid email'))
    .min(0)
    .max(1, 'You can only assign 1 recruiters'),
});

const ZoomAccountSetting = ({
  userId,
  phoneNumber,
  accountId,
  initialAssignedRecruiters,
  refreshLocalStorage,
}: IAccountSetting) => {
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [recruiterSuggestions, setRecruiterSuggestions] = useState<string[]>([]);
  const [assignedUsers, setAssignedusers] = useState<IAssignedUsers[]>(
    mapSharedAccount(initialAssignedRecruiters)
  );
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [reset, setreset] = useState<boolean>(false);

  const defaultValues = {
    assignedRecruiters: initialAssignedRecruiters,
    searchQuery: '',
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });
  const { getValues } = methods;

  const handleInput = async (input: string) => {
    const response = await postAdvancedQuery({
      ...recruiterAdvancedQueryPayload,
      recruiterEmailPhrase: input,
      recruiterFirstNamePhrase: input,
      recruiterLastNamePhrase: input,
    });
    setRecruiterSuggestions([]);
    response.data.map((recruiter: IRecruiterDTO) =>
      setRecruiterSuggestions((prev) => [...prev, recruiter.email])
    );
    setreset(false);
  };
  const assignRecruiter = async () => {
    setreset(true);
    const selected = methods.getValues('assignedRecruiters');
    const response = await sharePhoneNumber({
      ...sharePhoneNumberPayload,
      accountId: accountId,
      emails: selected,
      phoneNumber: phoneNumber,
    });
    response.data.status === 'SHARED'
      ? setNotification(success('Phone Number shared with recruiter successfully!'))
      : setNotification(failed(response.data.status));
    refreshLocalStorage();
  };

  const removeUser = async (userId: string) => {
    const payload: IWorkForceUser = {
      phoneNumber: phoneNumber,
      accountId: accountId,
      emails: [userId],
    };

    const response = await removeWorkForceUser(payload);

    response.data.status === 'REMOVED'
      ? setNotification(success('Workforce user removed successfully!'))
      : setNotification(failed(response.data.status));
    refreshLocalStorage();
  };

  const columns: GridColDef[] = [
    {
      headerName: 'Shared Workforce Users',
      field: 'id',
      flex: 4,
      renderCell: (params) => (
        <WsTypography variant={WsTypoVariants.BODY1}>{params.id}</WsTypography>
      ),
    },
    {
      headerName: '',
      field: 'owners',
      flex: 1,

      renderCell: (params) => (
        <IconButton
          onClick={() => removeUser(params.row.id)}
          disableRipple
          disabled={params.id === userId}
        >
          <RiDeleteBinLine />
        </IconButton>
      ),
    },
  ];

  const handleSearch = () => {
    const searchText = getValues('searchQuery');
    const results = assignedUsers.filter((item) => item.id?.includes(searchText));
    setAssignedusers(results);
  };
  const handleClear = () => {
    setAssignedusers(mapSharedAccount(initialAssignedRecruiters));
  };

  useEffect(() => {
    setAssignedusers(mapSharedAccount(initialAssignedRecruiters));
  }, [initialAssignedRecruiters]);

  return (
    <FormProvider {...methods}>
      <Grid container spacing={3} direction="column" sx={{ p: 2 }}>
        <Grid item>
          <WsTypography variant={WsTypoVariants.TITLE} bold>
            Account Credentials
          </WsTypography>
        </Grid>

        <Grid item xs={6} sm={6} width={450}>
          <TextField fullWidth variant="outlined" value={userId} size="small" disabled />
        </Grid>

        <Grid item>
          <Divider />
        </Grid>

        <Grid item>
          <WsTypography variant={WsTypoVariants.TITLE} bold>
            Add User
          </WsTypography>
          <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
            Provide Zoom account access to workforce users.
          </WsTypography>
        </Grid>

        <Grid item container spacing={2} alignItems="center">
          <Grid item xs={9}>
            <FormControl fullWidth size="small">
              <WsTypography variant={WsTypoVariants.BODY2}>
                Select Workforce User to Assign Phone Number : {phoneNumber}
              </WsTypography>
              <WsAutoComplete
                name="assignedRecruiters"
                fullWidth
                size="small"
                multiple={false}
                initialValue={initialAssignedRecruiters}
                optionsInStringArray={recruiterSuggestions}
                onValueEnter={handleInput}
                resetValue={reset}
                onTagsChange={(tags) => methods.setValue('assignedRecruiters', tags)}
              />
            </FormControl>
          </Grid>

          <Grid item xs={3} sx={{ mt: 2 }}>
            <WsButton type="submit" size="large" onClick={assignRecruiter}>
              Add User
            </WsButton>
          </Grid>
        </Grid>
        <Grid item>
          <WsTypography variant={WsTypoVariants.TITLE}>User</WsTypography>
        </Grid>

        <Grid item container spacing={2} alignItems="center">
          {initialAssignedRecruiters.length > 0 ? (
            <>
              <Grid item xs={6}>
                <SearchBarNew
                  size="small"
                  name="searchQuery"
                  placeholder="Search"
                  type="submit"
                  onClear={handleClear}
                  onSearch={handleSearch}
                />
              </Grid>
              <Grid item xs={12}>
                <WsDataGrid
                  autoHeight
                  rows={assignedUsers}
                  columns={columns}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <WsAddUserCard />
            </Grid>
          )}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default ZoomAccountSetting;
